<template>
    <div class="main">
        <div class="text-center">
            <h2> {{$t('OUTSTANDING_ORDERS_TITLE')}} </h2>
        </div>

        <div class="divider--container background--paper">
            <div class="outstanding-orders">
                <table class="table" v-if="!selectedOrder">
                    <thead>
                        <th>{{ $t('OUTSTANDING_ORDERS_DATE') }}</th>
                        <th>{{ $t('OUTSTANDING_ORDERS_COST') }}</th>
                        <th>{{ $t('OUTSTANDING_ORDERS_PAY_STATUS') }}</th>
                    </thead>
                    <tbody>
                        <tr v-for="(order, index) in outstandingOrders" :key="index">
                            <td>{{order.delivery_date}}</td>
                            <td>{{order.cost}}</td>
                            <td>{{order.payment_status === 'paid' ? $t('OUTSTANDING_ORDERS_PAID') : $t('OUTSTANDING_ORDERS_NOT_PAID')}}</td>
                        </tr>
                    </tbody>
                </table>

                <button v-if="outstandingOrders.length && !selectedOrder" class="btn btn--primary btn--half mb-5" @click="selectAllOrders()">{{$t('OUTSTANDING_ORDERS_PAY_ALL_ORDERS')}}</button>

                <div v-if="selectedOrder" class="outstanding-orders--stripe">
                    <stripe-payment-request-button v-if="showPaymentRequestButton" :setupIntent="setupIntent" @pay="payForOrder" isOutstandingPayments="true"/>

                    <stripe-elements-form
                        v-if="stripeMounted"
                        ref="cardForm"
                        :full-name="fullName"
                        :billing-address="deliveryAddress || billingAddress"
                        :has-been-requested="false"/>
                </div>

                <div v-if="selectedOrder" class="text-center">
                    <button v-if="stripeMounted" @click="payOutstandingOrdersByStripe" class="btn btn--secondary text-sm mb-4">{{$t('OUTSTANDING_ORDERS_PAY_NOW')}}</button>
                    <button @click="selectOrder(null)" class="btn btn--secondary text-sm">{{$t('OUTSTANDING_ORDERS_CANCEL')}}</button>
                </div>
            </div>

            <div class="divider-white--top"></div>
            <div class="divider-white--bottom"></div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PaymentService from '@/services/payments/PaymentService';
import StripeElementsForm from '@/components/forms/cardDetails/StripeElementsForm';
import StripePaymentRequestButton from '@/components/forms/cardDetails/StripePaymentRequestButton';

let paymentService = new PaymentService();

export default {
    name: 'OutstandingOrders',
    data() {
        return {
            selectedOrder: null,
        };
    },
    beforeCreate() {
        this.$store.dispatch('cookies/mountStripe');
    },

    created() {
        this.cardFullName = this.fullName;

        this.fetchStripeSetupIntent({ email: this.email, gateway: 'Stripe' });
    },

    mounted() {
        this.$store.dispatch('account/fetchOutstandingOrders');

        this.is3DSEnabled && this.fetchStripeSetupIntent({ email: this.email, gateway: this.paymentGateway, payment_method: 'card' });
    },
    methods: {
        ...mapActions({
            payOutstandingOrders: 'account/payOutstandingOrders',
            fetchStripeSetupIntent: 'account/fetchStripeSetupIntent',
        }),
        go(route) {
            this.$router.push({ name: route });
        },
        selectAllOrders() {
            this.selectedOrder = this.outstandingOrders;
        },
        selectOrder(order) {
            this.selectedOrder = order;
        },
        payForOrder(paymentToken, paymentMethod, paymentGateway) {
            let orderIds = Array.isArray(this.selectedOrder) ? this.selectedOrder.map((item) => item.id) : [this.selectedOrder.id];
            this.payOutstandingOrders({
                order_ids: orderIds,
                token: paymentToken,
                payment_method: paymentMethod,
                gateway: paymentGateway,
            }).then(() => {
                this.$store.dispatch('account/fetchOutstandingOrders');
                this.selectedOrder = null;
            }).catch(error => {
                this.$root.$emit('payment-error', error);
            });
        },
        payOutstandingOrdersByStripe() {
            this.$refs.cardForm.handleScaCardSubmission()
                .then(tokenData => {
                    paymentService.updatePaymentSource(this.userId, tokenData.setupIntentId, tokenData.setupIntentId, this.cardFullName)
                        .then(() => {
                            this.$store.dispatch('account/fetchUserData', true);
                            // redirect to dashboard
                            this.$router.push({ name: 'account.dashboard' });
                        })
                        .catch(error => {
                            this.toggleProcessing();
                            this.$store.commit('account/setSetupIntent', null);
                            this.fetchStripeSetupIntent({ email: this.email, gateway: 'Stripe' });
                            const hasAmex = error.response?.data?.error.includes('Amex');
                            if (hasAmex) return this.error = this.$t('AMERICAN_EXPRESS_NOT_SUPPORTED');
                            this.error = error.response.data.error;
                        });
                });
        },
    },
    computed: {
        ...mapGetters({
            outstandingOrders: 'account/getOutstandingOrders',
            stripeMounted: 'cookies/isStripeMounted',
            fullName: 'account/getFullName',
            paymentGateway: 'payment/getStripeGateway',
            setupIntent: 'account/getSetupIntent',
            is3DSEnabled: 'payment/is3DSEnabled',
            email: 'account/getEmail',
            isfetchingSetupIntent: 'account/isfetchingSetupIntent',
            billingAddress: 'payment/getBillingAddress',
            userId: 'account/getUserId',
        }),
        orderValue() {
            if (!this.selectedOrder) {
                return 0;
            }
            if (Array.isArray(this.selectedOrder)) {
                const values = this.selectedOrder.map((item) => item.cost.split(' ')[1]);
                return values.reduce((sum, value) => sum + parseFloat(value), 0);
            }
            return this.selectedOrder.cost.split(' ')[1];
        },
        useWorkFlows() {
            return process.env.VUE_APP_USE_WORKFLOWS_ENABLED;
        },
        billingAddress: {
            get() {
                return this.$store.getters['register/getBillingAddress'];
            },
            set(value) {
                this.$store.commit('register/setBillingAddress', value);
            },
        },
        deliveryAddress: {
            get() {
                return this.$store.getters['register/getDeliveryAddress'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddress', value);
            },
        },
        showPaymentRequestButton() {
            return this.is3DSEnabled && this.setupIntent;
        },
    },
    components: {
        StripeElementsForm,
        StripePaymentRequestButton,
    },
};
</script>

<style scoped lang="scss">
.outstanding-orders {
    width: 100%;
    margin: 0 auto;
    padding: 30px;
    box-sizing: border-box;
    text-align: center;
    .table {
        background-color: white;
        border: 1px solid black;
        text-align: center;
        margin: 20px auto;
        th, td {
            padding: 15px;
        }
    }
    &--stripe {
        background-color: white;
        border: 1px solid black;
        text-align: center;
        margin: 20px auto;
        padding: 20px;
    }
    &--stripe {
        width: 400px;
    }
}
</style>
